import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "./Admin.css";
import {
  AgeingChart,
  DeliveryChart,
  DemandChart,
  RankedBarChart,
  TrendChart,
  deliverySummary,
  demandSummary,
  rankedSummary,
  trendSummary,
} from "./admin/components/charts";
import { KpiCard, StatCard } from "./admin/components/kpis";
import { DistributorsTable, FilterBar, MiniList, OpenDemandsTable, RecentOrdersTable, SplitMeter } from "./admin/components/panels";
import { Card, ChartPanel, Notice, Skeleton } from "./admin/components/primitives";
import { count, money, percent, prettyDate, prettyTime } from "./admin/format";
import { useAdminDashboard, useDashboardPeriod } from "./admin/hooks";
import { usePalette } from "./admin/palette";
import { DashboardSwitcher } from "./admin/components/switcher";
import { trailingYear } from "./admin/periods";
import { selectDashboard } from "./admin/selectors";

const CHART_HEIGHT = { hero: 320, standard: 280, ranked: 320 };

const Admin = () => {
  const palette = usePalette();
  const { preset, range, selectPreset, setBound, setRange } = useDashboardPeriod();
  const { data, error, loading, refreshing, updatedAt, reload } = useAdminDashboard(range);
  const view = useMemo(() => selectDashboard(data, range), [data, range]);

  const { kpi, catalog, trend, delivery, payment, demand } = view;
  const periodLabel = `${prettyDate(range.from)} – ${prettyDate(range.to)}`;
  const jumpTo = trailingYear(view.lastDate);

  const status = loading ? "Loading dashboard…" : refreshing ? "Updating…" : updatedAt ? `Updated ${prettyTime(updatedAt)}` : "";

  const kpis = [
    { key: "sales", label: "Primary sales", value: money(kpi.sales?.value), delta: kpi.sales?.change_pct, color: palette.sales },
    { key: "collection", label: "Collection", value: money(kpi.collection?.value), delta: kpi.collection?.change_pct, color: palette.collection },
    { key: "due", label: "Due raised", value: money(kpi.due?.value), delta: kpi.due?.change_pct, color: palette.pending, invert: true },
    { key: "orders", label: "Invoices", value: count(kpi.orders?.value), delta: kpi.orders?.change_pct, color: palette.volume },
  ];

  const stats = [
    {
      key: "aov",
      label: "Average invoice value",
      value: money(kpi.avg_order_value?.value),
      delta: kpi.avg_order_value?.change_pct,
      caption: "per invoice",
    },
    {
      key: "distributors",
      label: "Distributors billed",
      value: count(kpi.distributors_billed?.value),
      delta: kpi.distributors_billed?.change_pct,
      caption: `of ${count(catalog.distributors)} on file`,
    },
    {
      key: "rate",
      label: "Collection rate",
      value: percent(view.collectionRate),
      caption: `${money(kpi.collection?.value)} of ${money(kpi.sales?.value)} billed`,
    },
    {
      key: "receivable",
      label: "Open receivable",
      hint: "All time",
      value: money(catalog.total_receivable),
      caption: `less ${money(catalog.total_advance)} advance → ${money(catalog.total_outstanding)} net`,
    },
  ];

  const demandKpis = [
    { key: "demanded", label: "Demand raised", value: money(demand.kpi.demanded?.value), delta: demand.kpi.demanded?.change_pct, color: palette.volume },
    { key: "sheets", label: "Demand sheets", value: count(demand.kpi.sheets?.value), delta: demand.kpi.sheets?.change_pct, color: palette.sales },
    { key: "invoiced", label: "Converted to orders", value: money(demand.kpi.invoiced?.value), delta: demand.kpi.invoiced?.change_pct, color: palette.collection },
    {
      key: "open",
      label: "Open demand value",
      value: money(demand.kpi.open_value?.value),
      delta: demand.kpi.open_value?.change_pct,
      color: palette.pending,
      invert: true,
      seriesKey: "open",
    },
  ];

  const demandStats = [
    {
      key: "fulfilment",
      label: "Fulfilment rate",
      value: percent(demand.fulfilmentRate),
      caption: `${money(demand.conversion.invoiced)} invoiced of ${money(demand.conversion.demanded)} demanded`,
    },
    {
      key: "closed",
      label: "Demands closed",
      value: count(demand.kpi.closed_sheets?.value),
      delta: demand.kpi.closed_sheets?.change_pct,
      caption: `${money(demand.status.closed_value)} closed off · ${count(demand.status.open_sheets)} still open`,
    },
    {
      key: "draft",
      label: "Awaiting approval",
      value: count(demand.status.draft_sheets),
      caption: `${count(demand.status.locked_sheets)} locked for editing`,
    },
    {
      key: "backlog",
      label: "Open demand backlog",
      hint: "All time",
      value: money(demand.backlog.value),
      caption: `${count(demand.backlog.sheets)} sheet(s) open, oldest ${prettyDate(demand.backlog.oldest_date)}`,
    },
  ];

  return (
    <div className="dash">
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Sales Dashboard</h5>
        </div>

        <div className="am-pagebody">
          <header className="dash-header">
            <div>
              <h1 className="dash-title">Sales overview</h1>
              <p className="dash-subtitle">
                {periodLabel}
                {view.comparison && <span className="dash-muted"> · compared {view.comparison}</span>}
              </p>
            </div>
            <div className="dash-header-actions">
              <DashboardSwitcher current="sales" />
              <span className="dash-status" role="status" aria-live="polite">
                {status}
              </span>
              <button type="button" className="dash-btn" disabled={loading || refreshing} onClick={reload}>
                <i className={`fa fa-refresh${refreshing ? " fa-spin" : ""}`} aria-hidden="true" /> Refresh
              </button>
            </div>
          </header>

          <FilterBar preset={preset} range={range} onPreset={selectPreset} onBound={setBound} />

          {error && (
            <Notice actionLabel="Retry" onAction={reload}>
              {error}
            </Notice>
          )}

          {view.outsideData && jumpTo && (
            <Notice actionLabel="Show last 12 months of activity" onAction={() => setRange(jumpTo)}>
              No primary sales recorded between {periodLabel}. The latest invoice on file is dated {prettyDate(view.lastDate)}.
            </Notice>
          )}

          <div className={`dash-body${refreshing ? " is-refetching" : ""}`} aria-busy={loading || refreshing}>
            {/* Headline KPIs */}
            <div className="dash-grid dash-grid--4">
              {kpis.map(({ key, ...tile }) => (
                <KpiCard key={key} {...tile} caption={view.comparison} series={trend} seriesKey={key} loading={loading} />
              ))}
            </div>

            {/* Supporting figures */}
            <div className="dash-grid dash-grid--4">
              {stats.map(({ key, ...stat }) => (
                <StatCard key={key} {...stat} loading={loading} />
              ))}
            </div>

            {/* Sales vs collection, fulfilment */}
            <div className="dash-grid dash-grid--8-4">
              <div className="dash-cell">
                <Card title="Sales billed vs cash collected" subtitle={`By ${view.granularity} · ${periodLabel}`}>
                  <ChartPanel loading={loading} empty={trend.length < 2} height={CHART_HEIGHT.hero} summary={trendSummary(trend)}>
                    <TrendChart data={trend} palette={palette} height={CHART_HEIGHT.hero} />
                  </ChartPanel>
                </Card>
              </div>

              <div className="dash-cell">
                <Card title="Order fulfilment & payment" subtitle="Share of billed value">
                  {loading ? (
                    <div className="dash-meter">
                      <Skeleton height={48} className="mg-b-15" />
                      <Skeleton height={48} />
                    </div>
                  ) : delivery.delivered + delivery.pending <= 0 && payment.paid + payment.unpaid <= 0 ? (
                    <ChartPanel empty />
                  ) : (
                    <>
                      <SplitMeter
                        parts={[
                          { label: "Delivered", value: delivery.delivered, color: palette.sales, note: `${count(delivery.deliveredOrders)} inv` },
                          { label: "Pending", value: delivery.pending, color: palette.pending, note: `${count(delivery.pendingOrders)} inv` },
                        ]}
                      />
                      <SplitMeter
                        parts={[
                          { label: "Paid", value: payment.paid, color: palette.collection },
                          { label: "Unpaid", value: payment.unpaid, color: palette.pending },
                        ]}
                      />
                      {payment.byType.length > 0 && <MiniList title="Billed by payment mode" rows={payment.byType} />}
                    </>
                  )}
                </Card>
              </div>
            </div>

            {/* Delivery over time, receivable ageing */}
            <div className="dash-grid dash-grid--7-5">
              <div className="dash-cell">
                <Card title="Delivered vs pending value" subtitle={`By ${view.granularity}, share of invoice value`}>
                  <ChartPanel loading={loading} empty={trend.length === 0} height={CHART_HEIGHT.standard} summary={deliverySummary(trend)}>
                    <DeliveryChart data={trend} palette={palette} height={CHART_HEIGHT.standard} />
                  </ChartPanel>
                </Card>
              </div>

              <div className="dash-cell">
                <Card title="Outstanding receivable by age" subtitle="All open invoices, oldest on the right">
                  <ChartPanel
                    loading={loading}
                    empty={view.ageing.length === 0}
                    emptyText="Nothing outstanding."
                    height={CHART_HEIGHT.standard}
                    summary={rankedSummary(view.ageing, 0)}
                  >
                    <AgeingChart data={view.ageing} palette={palette} height={CHART_HEIGHT.standard} />
                  </ChartPanel>
                </Card>
              </div>
            </div>

            {/* Products and territories */}
            <div className="dash-grid dash-grid--7-5">
              <div className="dash-cell">
                <Card title="Top products by sales value" subtitle="Ranked over the selected period">
                  <ChartPanel
                    loading={loading}
                    empty={view.topProducts.rows.length === 0}
                    height={CHART_HEIGHT.ranked}
                    summary={rankedSummary(view.topProducts.rows, view.topProducts.total)}
                  >
                    <RankedBarChart
                      data={view.topProducts.rows}
                      total={view.topProducts.total}
                      palette={palette}
                      height={CHART_HEIGHT.ranked}
                      labelWidth={170}
                      labelChars={24}
                    />
                  </ChartPanel>
                </Card>
              </div>

              <div className="dash-cell">
                <Card title={`${view.geography.dimension} wise sales`} subtitle="Top performing territories">
                  <ChartPanel
                    loading={loading}
                    empty={view.geography.rows.length === 0}
                    emptyText="No invoice in this period carries a region or area, so there is nothing to break down by territory."
                    height={CHART_HEIGHT.ranked}
                    summary={rankedSummary(view.geography.rows, view.geography.total)}
                  >
                    <RankedBarChart
                      data={view.geography.rows}
                      total={view.geography.total}
                      palette={palette}
                      height={CHART_HEIGHT.ranked}
                      labelWidth={120}
                      labelChars={18}
                    />
                  </ChartPanel>
                </Card>
              </div>
            </div>

            {/* Demand sheets: raised, converted, closed, still open */}
            <div className="dash-section">
              <h2 className="dash-section-title">Demand &amp; order pipeline</h2>
              <p className="dash-section-sub">
                Demand sheets raised in this period and how much of them has turned into primary sales orders.
              </p>
            </div>

            <div className="dash-grid dash-grid--4">
              {demandKpis.map(({ key, seriesKey, ...tile }) => (
                <KpiCard
                  key={key}
                  {...tile}
                  caption={view.comparison}
                  series={demand.trend}
                  seriesKey={seriesKey || key}
                  loading={loading}
                />
              ))}
            </div>

            <div className="dash-grid dash-grid--4">
              {demandStats.map(({ key, ...stat }) => (
                <StatCard key={key} {...stat} loading={loading} />
              ))}
            </div>

            <div className="dash-grid dash-grid--8-4">
              <div className="dash-cell">
                <Card title="Demand converted vs still open" subtitle={`By ${view.granularity} · demanded value split by what has been invoiced`}>
                  <ChartPanel
                    loading={loading}
                    empty={!demand.hasActivity}
                    emptyText="No demand sheet was raised in this period."
                    height={CHART_HEIGHT.standard}
                    summary={demandSummary(demand.trend)}
                  >
                    <DemandChart data={demand.trend} palette={palette} height={CHART_HEIGHT.standard} />
                  </ChartPanel>
                </Card>
              </div>

              <div className="dash-cell">
                <Card title="Demand conversion" subtitle="Share of demanded value">
                  {loading ? (
                    <div className="dash-meter">
                      <Skeleton height={48} className="mg-b-15" />
                      <Skeleton height={48} />
                    </div>
                  ) : !demand.hasActivity ? (
                    <ChartPanel empty emptyText="No demand sheet was raised in this period." />
                  ) : (
                    <>
                      <SplitMeter
                        parts={[
                          { label: "Invoiced", value: demand.conversion.invoiced, color: palette.collection, note: `${count(demand.conversion.orders)} order(s)` },
                          { label: "Open", value: demand.conversion.open, color: palette.pending },
                        ]}
                      />
                      <MiniList
                        title="Sheets by state"
                        format={count}
                        rows={[
                          { name: "Open", value: demand.status.open_sheets },
                          { name: "Closed", value: demand.status.closed_sheets },
                          { name: "Awaiting approval", value: demand.status.draft_sheets },
                          { name: "Locked", value: demand.status.locked_sheets },
                        ]}
                      />
                      <MiniList
                        title="Money on the sheets"
                        rows={[
                          { name: "Advance taken", value: demand.status.advance },
                          { name: "Due raised", value: demand.status.due },
                        ]}
                      />
                    </>
                  )}
                </Card>
              </div>
            </div>

            <div className="dash-grid dash-grid--7-5">
              <div className="dash-cell">
                <Card
                  title="Open demand sheets"
                  subtitle="Largest value still to invoice, regardless of period"
                  action={
                    <Link className="dash-btn" to="/demand-sheet">
                      All demands
                    </Link>
                  }
                  flush
                >
                  <OpenDemandsTable rows={demand.openSheets} loading={loading} />
                </Card>
              </div>

              <div className="dash-cell">
                <Card title="Open demand by age" subtitle="Value still to invoice, oldest on the right">
                  <ChartPanel
                    loading={loading}
                    empty={demand.ageing.length === 0}
                    emptyText="Every demand sheet is fully invoiced or closed."
                    height={CHART_HEIGHT.standard}
                    summary={rankedSummary(demand.ageing, 0)}
                  >
                    <AgeingChart data={demand.ageing} palette={palette} height={CHART_HEIGHT.standard} />
                  </ChartPanel>
                </Card>
              </div>
            </div>

            {/* Tables */}
            <div className="dash-grid dash-grid--halves">
              <div className="dash-cell">
                <Card
                  title="Top distributors"
                  subtitle="By billed value over the selected period"
                  action={
                    <Link className="dash-btn" to="/distributors">
                      All distributors
                    </Link>
                  }
                  flush
                >
                  <DistributorsTable rows={view.topDistributors} loading={loading} />
                </Card>
              </div>

              <div className="dash-cell">
                <Card
                  title="Latest invoices"
                  subtitle="Most recent primary sales, regardless of period"
                  action={
                    <Link className="dash-btn" to="/primary-sales">
                      All sales
                    </Link>
                  }
                  flush
                >
                  <RecentOrdersTable rows={view.recentOrders} loading={loading} />
                </Card>
              </div>
            </div>
          </div>
        </div>

        <footer className="am-footer">
          <span>
            &copy; {new Date().getFullYear()}{" "}
            <a href="http://limerickbd.com/" target="_blank" rel="noopener noreferrer">
              Limerick Resources Limited
            </a>
            . All rights reserved.
          </span>
        </footer>
      </div>
    </div>
  );
};

export default Admin;
